import { useMediaQuery } from 'react-responsive';

import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import AvatarIcon from 'honeydew/icons/Avatar';
import Tag from 'honeydew/Tag';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { calculateFullYears } from 'utils/dateUtils';

/**
 * Avatar component that displays user account information including avatar icon and account name.
 * Shows a special "year club" tag for members who have been subscribed for one or more years.
 *
 * @component
 * @returns {JSX.Element} The rendered Avatar component with user info and optional year club badge
 *
 * @example
 * // Basic usage in account layout
 * <Avatar />
 *
 * @description
 * - Displays different avatar sizes based on screen size (55px mobile, 90px desktop)
 * - Shows account name from subscription shipping address or defaults to "My Account"
 * - Calculates membership years and displays year club badge for members with 1+ years
 * - Adapts layout for mobile (horizontal) vs desktop (vertical) display
 * - Uses theme-aware styling based on Misfits vs Imperfect Foods branding
 */

export function Avatar() {
    const { isMisfitsTheme } = useGlobalStore();
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { data: subscription } = useSubscriptionQuery();
    const { membership } = useAccountPlusMembershipHook();

    const accountName = subscription?.shippingAddr?.firstName
        ? `${subscription.shippingAddr.firstName}'s Account`
        : 'My Account';

    const numYears = calculateFullYears(membership?.memberSinceDate) || 0;
    const oneYearClub = numYears >= 1;

    return (
        <div className="flex items-center md:flex-col">
            <AvatarIcon isMisfits={isMisfitsTheme} width={mobile ? 55 : 90} oneYearClub={oneYearClub} />
            <div className="ml-20 text-left font-grotesk-bold md:ml-0 md:mt-20 md:text-center">
                {accountName}
                {oneYearClub && (
                    <div className="mt-5">
                        <Tag bgColor="plusMembership">
                            <span className="uppercase">{numYears} year club</span>
                        </Tag>
                    </div>
                )}
            </div>
        </div>
    );
}
